import React, { useEffect, useRef, useState } from "react";
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, InputAdornment, TextField, Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import { useCreateCompanyMutation } from "../../Api/attoDeskApi";
import { useNotifier } from "../../Core/Notifier";
import { extractBackendError } from "../../Core/extractBackendError";

/**
 * Lightweight "quick create" dialog for the company picker in the
 * Device Create page (and any other picker that eventually needs it).
 *
 * By design this dialog only collects the fields that are strictly
 * required to make a company usable in a picker:
 *   - name (mandatory, unique on the server)
 *   - email + phone (both mandatory since 2026-06-25)
 *   - optional website + HST for convenience
 *
 * The full-page /company/create form is untouched — operators can still
 * open it to fill in logo, watermark, address, description, invoice
 * footer, etc. This dialog and the page hit the SAME endpoint
 * (POST /api/company) and therefore share the same validation,
 * uniqueness rules, and permission gate on the backend.
 *
 * Callback contract: on success, `onCreated` fires with { id, companyName }
 * and the dialog closes itself. The parent is responsible for refetching
 * its company list and selecting the new row.
 */

const labelSx = { fontSize: 12, fontWeight: 600, color: "#334155", mb: 0.75 };
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2, fontSize: 13, bgcolor: "#fff",
    "& fieldset": { borderColor: "#e2e8f0" },
    "&:hover fieldset": { borderColor: "#cbd5e1" },
    "&.Mui-focused fieldset": { borderColor: "#00838f", borderWidth: 1.5 },
  },
};

// Same formatter the full Company Create page uses so the two flows
// produce identical phone strings.
const formatPhone = (value: string): string => {
  const digits = value.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

interface CreatedCompany {
  id: number;
  companyName: string;
}

interface CompanyQuickCreateDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (company: CreatedCompany) => void;
  /** Optional starting text used when the operator was typing in the
   *  picker filter before clicking "Add". Pre-fills the name field. */
  initialName?: string;
}

const CompanyQuickCreateDialog: React.FC<CompanyQuickCreateDialogProps> = ({
  open, onClose, onCreated, initialName,
}) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [createCompany, { isLoading }] = useCreateCompanyMutation();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [hst, setHst] = useState("");
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const submitInFlightRef = useRef(false);

  // Reset the form each time the dialog opens so a previous attempt
  // doesn't linger. Pre-seed the name if the picker passed one in.
  useEffect(() => {
    if (open) {
      setName(initialName?.trim() ?? "");
      setEmail("");
      setPhone("");
      setWebsite("");
      setHst("");
      setTouched({});
      submitInFlightRef.current = false;
    }
  }, [open, initialName]);

  const errors: Record<string, string> = {};
  if (!name.trim()) errors.name = "Company name is required";
  const emailTrim = email.trim();
  if (!emailTrim) errors.email = "Email is required";
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) errors.email = "Invalid email";
  const phoneDigits = phone.replace(/\D/g, "");
  if (!phoneDigits) errors.phone = "Phone is required";
  else if (phoneDigits.length < 10) errors.phone = "Phone must have 10 digits";

  const valid = Object.keys(errors).length === 0;

  const handleSubmit = async () => {
    setTouched({ name: true, email: true, phone: true });
    if (!valid) return;
    if (submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    try {
      const res: any = await createCompany({
        companyName: name.trim(),
        companyEmail: emailTrim,
        companyMobileNumber: phone,
        companyWebSiteLink: website.trim() || null,
        companyHstNo: hst.trim() || null,
      }).unwrap();

      if (!res?.status) {
        showErrorMessage(res?.message || "Could not create company");
        return;
      }
      const data = res.data || {};
      const created: CreatedCompany = {
        id: Number(data.id),
        companyName: String(data.companyName ?? name.trim()),
      };
      showMessage("Company created");
      onCreated(created);
      onClose();
    } catch (e: any) {
      showErrorMessage(extractBackendError(e, "Could not create company"));
    } finally {
      submitInFlightRef.current = false;
    }
  };

  return (
    <Dialog open={open} onClose={isLoading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1.25, pr: 5, pb: 1 }}>
        <BusinessIcon sx={{ color: "#00838f", fontSize: 20 }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontSize={16} fontWeight={800} color="#0f172a">
            New Company
          </Typography>
          <Typography fontSize={11} color="#64748b" mt={0.25}>
            Enter the essentials — you can add logo, address and more later from Companies.
          </Typography>
        </Box>
        <IconButton onClick={onClose} disabled={isLoading}
          sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        <Box>
          <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
            Company Name{" "}
            <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>
          </Typography>
          <TextField
            autoFocus fullWidth size="small" placeholder="Enter company name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, name: true }))}
            error={touched.name && !!errors.name}
            helperText={touched.name && errors.name}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
            Email{" "}
            <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>
          </Typography>
          <TextField
            fullWidth size="small" type="email" placeholder="company@example.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onBlur={() => setTouched((t) => ({ ...t, email: true }))}
            error={touched.email && !!errors.email}
            helperText={touched.email && errors.email}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <EmailIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              </InputAdornment>
            ) }}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Typography sx={{ ...labelSx, display: "flex", alignItems: "center", gap: 0.5 }}>
            Phone{" "}
            <Typography component="span" sx={{ color: "#ef4444", fontSize: 11 }}>*</Typography>
          </Typography>
          <TextField
            fullWidth size="small" placeholder="(555) 000-0000"
            value={phone}
            onChange={(e) => setPhone(formatPhone(e.target.value))}
            onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
            error={touched.phone && !!errors.phone}
            helperText={touched.phone && errors.phone}
            InputProps={{ startAdornment: (
              <InputAdornment position="start">
                <PhoneIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
              </InputAdornment>
            ) }}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Typography sx={labelSx}>Website</Typography>
          <TextField
            fullWidth size="small" placeholder="https://example.com"
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            sx={{ ...inputSx, mb: 2 }}
          />

          <Typography sx={labelSx}>HST / Tax Number</Typography>
          <TextField
            fullWidth size="small" placeholder="HST number"
            value={hst}
            onChange={(e) => setHst(e.target.value)}
            sx={inputSx}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button type="button" onClick={onClose} disabled={isLoading}
          sx={{ textTransform: "none", fontWeight: 600, color: "#64748b" }}>
          Cancel
        </Button>
        <Button
          type="button" variant="contained" disableElevation
          disabled={!valid || isLoading}
          onClick={handleSubmit}
          startIcon={isLoading ? <CircularProgress size={14} sx={{ color: "#fff" }} /> : null}
          sx={{
            textTransform: "none", fontWeight: 700, fontSize: 13, px: 3,
            background: "linear-gradient(135deg, #00838f 0%, #00acc1 100%)",
            "&:hover": { background: "linear-gradient(135deg, #006064 0%, #00838f 100%)" },
            "&.Mui-disabled": { bgcolor: "#e2e8f0", color: "#94a3b8" },
          }}
        >
          {isLoading ? "Creating..." : "Create Company"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CompanyQuickCreateDialog;
